// Marketplace listings prefetch — fires on Rent-nav hover so the data is
// already in flight by the time the renter clicks. RentAVan reads the
// cached payload on mount instead of starting a fresh round-trip.
//
// Only the slow-ish queries (vehicles + photos) live here. Ratings,
// bookings, availability blocks, promos, categories all stay in RentAVan
// because they're fast and per-render anyway.
import { supabase } from "@/integrations/supabase/client";

const TTL_MS = 0; // Caching disabled — every prefetchListings() call
                  // round-trips to Supabase. The hover-prefetch on the
                  // Rent nav link still kicks the request early, but
                  // the result is never reused across calls. RentAVan
                  // also calls clearPrefetchCache() on mount so any
                  // in-flight promise is dropped too.

export interface PrefetchedPhoto {
  photo_url: string;
  is_primary: boolean;
  sort_order: number | null;
}

export interface PrefetchedListings {
  vehicles: any[];
  photos: Record<string, PrefetchedPhoto[]>;
}

let cached: { result: PrefetchedListings; at: number } | null = null;
let pending: Promise<PrefetchedListings> | null = null;

// Listing-grid + map-marker columns only. `description` was dropped in
// the May 2026 perf pass — it's pulled per-vehicle on VehicleDetail and
// never read by the grid, so shipping it here was wasted bandwidth on
// every Rent-page load. Any new column the grid or markers consume must
// be added explicitly; SELECT * is intentionally avoided.
const VEHICLE_COLS =
  "id, title, make, model, year, city, state, zip_code, daily_price, weekend_price, passenger_capacity, luggage_capacity, vehicle_type, is_approved, created_at, booking_mode, latitude, longitude, hide_exact_address, temp_city, temp_state, temp_location_start, temp_location_end, service_locations, delivery_option, delivery_available, vauren_select, personality_tags";

// Upcoming Friday–Sunday window. If today is mid-week, this is the
// coming weekend; if today is already in the weekend window, it stays
// pointing at the *next* Fri-Sun so the badge never reads "available
// this weekend" while the weekend is already passing.
const upcomingWeekend = (): { friday: string; sunday: string } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dow = today.getDay(); // 0=Sun .. 6=Sat
  // Days to the next Friday. (5 - dow + 7) % 7 returns 0 on Friday;
  // we bump that to 7 so a Friday morning shows "available *next*
  // weekend" rather than the weekend that's already begun by noon.
  let offset = (5 - dow + 7) % 7;
  if (offset === 0) offset = 7;
  const friday = new Date(today);
  friday.setDate(today.getDate() + offset);
  const sunday = new Date(friday);
  sunday.setDate(friday.getDate() + 2);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { friday: fmt(friday), sunday: fmt(sunday) };
};

const fetchListings = async (): Promise<PrefetchedListings> => {
  const weekend = upcomingWeekend();

  const [vRes, pRes, bRes] = await Promise.all([
    (supabase.from("vehicles").select(VEHICLE_COLS) as any)
      .eq("is_approved", true)
      .eq("is_active", true)
      .eq("vehicle_type", "sprinter_van")
      .order("created_at", { ascending: false }),
    // Grid card needs only the primary photo per vehicle. The
    // vehicle_photos_one_primary partial unique index guarantees at
    // most one is_primary=true row per vehicle, so filtering on it
    // yields exactly one row per vehicle. A flat `.limit(1)` would
    // cap the whole result set to one row total across the marketplace
    // — that's not what we want.
    supabase
      .from("vehicle_photos")
      .select("vehicle_id, photo_url, is_primary, sort_order")
      .eq("is_primary", true)
      .order("is_primary", { ascending: false, nullsFirst: false })
      .order("sort_order", { ascending: true, nullsFirst: false }),
    // Weekend-availability lookup — every booking that overlaps the
    // upcoming Fri-Sun window and is still live (confirmed / active).
    // A single round-trip across the catalogue is cheaper than per-card
    // checks; we just bucket into a Set client-side.
    supabase
      .from("rental_bookings")
      .select("vehicle_id, start_date, end_date, status")
      .in("status", ["confirmed", "active"])
      .lte("start_date", weekend.sunday)
      .gte("end_date", weekend.friday),
  ]);

  const blockedThisWeekend = new Set<string>();
  (bRes.data || []).forEach((b: { vehicle_id: string }) => {
    if (b?.vehicle_id) blockedThisWeekend.add(b.vehicle_id);
  });

  // Annotate each vehicle with the derived weekend_available flag so
  // VehicleCard can render the "Available this weekend" chip without
  // a per-card query.
  const vehicles = ((vRes.data || []) as any[]).map((v) => ({
    ...v,
    weekend_available: !blockedThisWeekend.has(v.id),
  }));

  // The query orders is_primary DESC NULLS LAST, sort_order ASC NULLS LAST
  // at the DB. With the vehicle_photos_one_primary partial unique index in
  // place, every vehicle has exactly one is_primary=true row, so the first
  // entry per vehicle is always the primary. No JS sort needed.
  const photos: Record<string, PrefetchedPhoto[]> = {};
  pRes.data?.forEach((p: any) => {
    if (!photos[p.vehicle_id]) photos[p.vehicle_id] = [];
    photos[p.vehicle_id].push({
      photo_url: p.photo_url,
      is_primary: p.is_primary === true,
      sort_order: typeof p.sort_order === "number" ? p.sort_order : null,
    });
  });
  return { vehicles, photos };
};

/**
 * Kick off a listings fetch and cache the result. Safe to call repeatedly —
 * a pending fetch is shared, and a fresh cached result is returned without
 * a new network call.
 */
export const prefetchListings = async (): Promise<PrefetchedListings> => {
  if (cached && Date.now() - cached.at < TTL_MS) return cached.result;
  if (pending) return pending;
  pending = fetchListings()
    .then((result) => {
      cached = { result, at: Date.now() };
      return result;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
};

/**
 * Synchronous read for consumers that have a synchronous code path
 * (e.g. setting initial state). Returns null when the cache is empty or
 * expired so the caller can fall back to the async path.
 */
export const readCachedListings = (): PrefetchedListings | null => {
  if (!cached || Date.now() - cached.at >= TTL_MS) return null;
  return cached.result;
};

/**
 * Hard-reset the cache. Called from RentAVan on mount so a page navigation
 * never reuses photo / primary data captured before a host edited a
 * listing. Also aborts any in-flight fetch reference so the next
 * `prefetchListings()` call starts fresh.
 */
export const clearPrefetchCache = (): void => {
  cached = null;
  pending = null;
};
